// Deep-link cards for platform artifacts created from the chat.
//
// When a tool call creates something real (a goal, an alert, a segment,
// a pipeline, a deployment), the backend attaches an `artifact` spec and
// this card renders it: type icon, name, status chip, and an "Open"
// action that navigates to the artifact's actual page. The point is
// wayfinding - the chat hands the user a door into the product instead
// of keeping them locked in the conversation.
//
// The milestone variant (a goal reaching `serving`) is the big one: a
// celebratory full-width card for the moment their first prediction
// goes live.

import React from 'react';
import { Box, Stack, Typography, alpha } from '@mui/material';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import {
  TrackChanges as GoalIcon,
  NotificationsActive as AlertIcon,
  Dataset as SegmentIcon,
  AccountTree as PipelineIcon,
  RocketLaunch as DeploymentIcon,
  ArrowForward as ArrowIcon,
  AutoAwesome as SparkIcon,
} from '@mui/icons-material';

import { surfacesFor } from './uiTokens';
import { useAnalystDock } from '../../analyst/dockState';


const TYPE_META = {
  goal:       { Icon: GoalIcon,       label: 'Goal' },
  alert:      { Icon: AlertIcon,      label: 'Alert' },
  segment:    { Icon: SegmentIcon,    label: 'Segment' },
  pipeline:   { Icon: PipelineIcon,   label: 'Pipeline' },
  deployment: { Icon: DeploymentIcon, label: 'Deployment' },
};

// Client route for each artifact type. Alerts live inside their goal's
// Alerts section; everything else has a first-class detail page.
export const routeForArtifact = (spec) => {
  if (!spec?.id) return null;
  switch (spec.artifact_type) {
    case 'goal':       return `/goals/${spec.id}`;
    case 'alert':      return spec.goal_id ? `/goals/${spec.goal_id}/alerts` : null;
    case 'segment':    return `/segments/${spec.id}`;
    case 'pipeline':   return `/pipeline/${spec.id}`;
    case 'deployment': return `/deployment/${spec.id}`;
    default:           return null;
  }
};

const STATUS_TONE = {
  serving:  'live',
  live:     'live',
  armed:    'live',
  failed:   'error',
  queued:   'busy',
  deploying: 'busy',
  discovering: 'busy',
  training: 'busy',
};

const statusChipSx = (t, tone) => {
  const dark = t.palette.mode === 'dark';
  const lime = t.brand?.lime || t.palette.primary.main;
  if (tone === 'live') {
    return {
      color: dark ? lime : (t.brand?.teal || t.palette.primary.main),
      borderColor: alpha(dark ? lime : (t.brand?.teal || '#197563'), 0.45),
      bgcolor: alpha(dark ? lime : '#6cb31f', 0.08),
    };
  }
  if (tone === 'error') {
    return {
      color: surfacesFor(t).danger,
      borderColor: alpha('#ff8a80', 0.4),
      bgcolor: alpha('#ff8a80', 0.07),
    };
  }
  const s = surfacesFor(t);
  return { color: s.muted, borderColor: s.border2, bgcolor: 'transparent' };
};

const StatusChip = ({ status }) => {
  if (!status) return null;
  const tone = STATUS_TONE[String(status).toLowerCase()] || 'busy';
  return (
    <Typography
      component="span"
      sx={(t) => ({
        px: 1, py: '2px',
        borderRadius: 999,
        border: '1px solid',
        fontSize: 10, fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
        flexShrink: 0,
        ...statusChipSx(t, tone),
      })}
    >
      {status}
    </Typography>
  );
};


const cardIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: none; }
`;

// The soft lime pulse behind the milestone card's icon.
const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(192, 251, 110, 0.22); }
  50%      { box-shadow: 0 0 0 9px rgba(192, 251, 110, 0); }
`;

// A quiet shine sweep across clickable cards — same idea as the login
// page's beta pill, but slower and fainter: enough to catch the eye
// once in a while, not enough to nag. The sweep itself takes ~1.6s of
// an 8s cycle; the rest is idle.
const cardShine = keyframes`
  0%        { transform: translateX(-100%); }
  20%, 100% { transform: translateX(100%); }
`;

const shineSx = (t) => ({
  position: 'relative',
  overflow: 'hidden',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: t.palette.mode === 'dark'
      ? 'linear-gradient(105deg, transparent 35%, rgba(192, 251, 110, 0.09) 50%, transparent 65%)'
      : 'linear-gradient(105deg, transparent 35%, rgba(108, 179, 31, 0.08) 50%, transparent 65%)',
    transform: 'translateX(-100%)',
    animation: `${cardShine} 8s ease-in-out infinite`,
    pointerEvents: 'none',
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&::after': { animation: 'none' },
  },
});


// Compact inline card: [icon] Title · type — [status] [Open →]
const StandardCard = ({ spec, route, onOpen }) => {
  const meta = TYPE_META[spec.artifact_type] || { Icon: SparkIcon, label: 'Artifact' };
  const { Icon } = meta;
  return (
    <Stack
      direction="row" alignItems="center" spacing={1.5}
      role={route ? 'button' : undefined}
      tabIndex={route ? 0 : undefined}
      onClick={route ? onOpen : undefined}
      onKeyDown={route ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
      sx={(t) => {
        const s = surfacesFor(t);
        return {
          my: 1, px: 1.75, py: 1.25,
          maxWidth: 480,
          borderRadius: '14px',
          border: `1px solid ${s.border2}`,
          background: t.palette.mode === 'dark'
            ? `linear-gradient(180deg, ${s.panel2}, ${s.panel})`
            : s.panel,
          boxShadow: t.palette.mode === 'dark' ? 'none' : '0 2px 10px rgba(40,50,35,0.06)',
          animation: `${cardIn} 260ms cubic-bezier(0.2, 0.7, 0.3, 1) both`,
          cursor: route ? 'pointer' : 'default',
          userSelect: 'none',
          transition: 'border-color 140ms, transform 140ms',
          // Clickable cards get the occasional shine so "Open" registers.
          ...(route ? shineSx(t) : {}),
          '&:hover': route ? {
            borderColor: t.brand?.teal || t.palette.primary.main,
            '& .artifact-open': { color: t.palette.mode === 'dark' ? (t.brand?.lime || '#c0fb6e') : t.palette.primary.main },
          } : {},
        };
      }}
    >
      <Box
        sx={(t) => {
          const dark = t.palette.mode === 'dark';
          // Dark mode uses the chart palette's light teal so the icon
          // reads on ink; light mode uses the brand teal proper.
          const teal = dark ? '#5ee6d0' : (t.brand?.teal || '#197563');
          return {
            width: 34, height: 34, flexShrink: 0,
            borderRadius: '10px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(teal, dark ? 0.13 : 0.1),
            color: teal,
          };
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={(t) => ({ fontSize: 13, fontWeight: 700, color: surfacesFor(t).text })}>
          {spec.title || meta.label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: '2px' }}>
          <Typography sx={(t) => ({ fontSize: 10.5, color: surfacesFor(t).faint })}>
            {meta.label}
          </Typography>
          <StatusChip status={spec.status} />
        </Stack>
      </Box>
      {route && (
        <Stack
          className="artifact-open"
          direction="row" alignItems="center" spacing={0.5}
          sx={(t) => ({ flexShrink: 0, color: surfacesFor(t).muted, transition: 'color 140ms' })}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'inherit' }}>Open</Typography>
          <ArrowIcon sx={{ fontSize: 14 }} />
        </Stack>
      )}
    </Stack>
  );
};


// The "your goal is live" moment - bigger, lime-accented, celebratory.
const MilestoneCard = ({ spec, route, onOpen }) => (
  <Box
    role={route ? 'button' : undefined}
    tabIndex={route ? 0 : undefined}
    onClick={route ? onOpen : undefined}
    onKeyDown={route ? (e) => { if (e.key === 'Enter') onOpen(); } : undefined}
    sx={(t) => {
      const s = surfacesFor(t);
      const dark = t.palette.mode === 'dark';
      const lime = t.brand?.lime || '#c0fb6e';
      return {
        my: 1.25, px: 2.25, py: 2,
        maxWidth: 520,
        borderRadius: '18px',
        border: `1px solid ${alpha(lime, dark ? 0.35 : 0.5)}`,
        background: dark
          ? `linear-gradient(135deg, ${alpha(lime, 0.07)}, ${s.panel} 55%)`
          : `linear-gradient(135deg, ${alpha('#6cb31f', 0.08)}, #ffffff 55%)`,
        boxShadow: dark
          ? `0 6px 30px ${alpha(lime, 0.10)}`
          : '0 4px 18px rgba(40,50,35,0.08)',
        animation: `${cardIn} 320ms cubic-bezier(0.2, 0.7, 0.3, 1) both`,
        cursor: route ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'border-color 140ms',
        '&:hover': route ? { borderColor: alpha(lime, 0.75) } : {},
      };
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1.75}>
      <Box
        sx={(t) => {
          const dark = t.palette.mode === 'dark';
          const lime = t.brand?.lime || '#c0fb6e';
          return {
            width: 44, height: 44, flexShrink: 0,
            borderRadius: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: dark ? alpha(lime, 0.14) : alpha('#6cb31f', 0.12),
            color: dark ? lime : (t.brand?.teal || t.palette.primary.main),
            animation: `${glowPulse} 2.4s ease-out infinite`,
          };
        }}
      >
        <GoalIcon sx={{ fontSize: 23 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={(t) => ({ fontSize: 14.5, fontWeight: 800, color: surfacesFor(t).text, lineHeight: 1.3 })}>
          Your goal is live
        </Typography>
        <Typography noWrap sx={(t) => ({ mt: '2px', fontSize: 12, color: surfacesFor(t).muted })}>
          {spec.title || 'Goal'} is serving predictions now
        </Typography>
      </Box>
      {route && (
        <Stack
          direction="row" alignItems="center" spacing={0.6}
          sx={(t) => {
            const dark = t.palette.mode === 'dark';
            const lime = t.brand?.lime || '#c0fb6e';
            return {
              flexShrink: 0,
              px: 1.75, py: 0.8,
              borderRadius: 999,
              bgcolor: dark ? lime : (t.brand?.teal || t.palette.primary.main),
              color: dark ? (t.brand?.ink || '#0a0c0a') : '#fff',
              fontWeight: 700,
            };
          }}
        >
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'inherit' }}>See it live</Typography>
          <ArrowIcon sx={{ fontSize: 14 }} />
        </Stack>
      )}
    </Stack>
  </Box>
);


const ArtifactCard = ({ spec }) => {
  const navigate = useNavigate();
  const { openDock } = useAnalystDock();
  if (!spec || !spec.artifact_type) return null;
  const route = routeForArtifact(spec);
  // Following a card out of the chat keeps the conversation beside the
  // page (docked right) - it only fully minimizes when the user
  // collapses it themselves.
  const onOpen = () => {
    if (!route) return;
    openDock();
    navigate(route);
  };
  const milestone = Boolean(spec.milestone)
    && spec.artifact_type === 'goal'
    && String(spec.status || '').toLowerCase() === 'serving';
  return milestone
    ? <MilestoneCard spec={spec} route={route} onOpen={onOpen} />
    : <StandardCard spec={spec} route={route} onOpen={onOpen} />;
};

export default ArtifactCard;
